"use client";

import React, { useEffect } from "react";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Quote, Sparkles } from "lucide-react";

/** Market-test (market-test-plan.md §3): compact, REAL peer proof answers the
 *  "is this too new/unknown?" objection without slowing the funnel (research/07
 *  CF5). Claim-safe: no ratings, no counts, no AggregateRating. */
const PROOF_EXPERIMENT_ID = "proof_premium_authentic_v1";

/**
 * AquaGlobe reviews strip — frosted-glass review cards. Claim-safe: short
 * quotes + traveler first names only, NO star counts, NO fabricated totals or
 * "trusted by N travelers" numbers. Glass-native.
 */
export default function ReviewsStrip() {
  const { t } = useTranslations();

  useEffect(() => {
    try {
      track({
        event_name: "review_module_viewed",
        event_id: `review_module_viewed:${PROOF_EXPERIMENT_ID}`,
        experiment_id: PROOF_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const reviews = [
    {
      quote: t("aqua.reviews.r1.quote", "Set up my plan before the flight and landed already online. Beautifully simple."),
      name: t("aqua.reviews.r1.name", "Maria L."),
    },
    {
      quote: t("aqua.reviews.r2.quote", "The QR code arrived by email right after checkout and activated in a minute."),
      name: t("aqua.reviews.r2.name", "James T."),
    },
    {
      quote: t("aqua.reviews.r3.quote", "I kept my home number for messages and used the eSIM just for data. Perfect."),
      name: t("aqua.reviews.r3.name", "Priya S."),
    },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" className="text-center mb-10" y={16}>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground inline-flex items-center gap-2">
          <Sparkles className="aqua-sparkle h-5 w-5" />
          {t("aqua.reviews.title", "Trusted by travelers")}
        </h2>
      </Reveal>

      <Stagger className="grid md:grid-cols-3 gap-5" staggerChildren={0.09} amount={0.2}>
        {reviews.map((review, i) => (
          <StaggerItem as="figure" y={16} key={i} className="aqua-glass rounded-3xl p-6 backdrop-blur flex flex-col">
            <Quote className="h-7 w-7 text-primary/40 mb-3" aria-hidden="true" />
            <blockquote className="text-sm text-foreground/90 flex-1">{review.quote}</blockquote>
            <figcaption className="mt-4 text-sm font-semibold text-foreground">{review.name}</figcaption>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}
